import { PencilIcon } from '@heroicons/react/24/solid';
import { ErrorBoundary } from '@sentry/react';
import { forwardRef, Ref } from 'react';

import { Block, LiveBlockAddress, Resource, ResourceType, ScriptureBlockType } from '../types';
import { BlockView } from './BlockView';
import { Button } from '../../ui/components/Button';
import { ResourceStatusIcons } from './ResourceStatusIcons';
import classNames from 'classnames';

type Props = {
  className?: string,
  liveAddress?: LiveBlockAddress,
  resource: Resource | null,
  onEdit?: () => void,
  onLineClick?: (blockIdx: number) => (lineGroupIdx: number) => (lineIdx: number) => void
}

const ResourceViewInner = ({ liveAddress, onEdit, onLineClick, resource }: Props) => {

  const tally: Record<string, number> = {};

  const getDescriptor = (block: Block) => {
    if (block.type === ScriptureBlockType.SCRIPTURE) {
      return '';
    }
    const blockType = block.type.valueOf();
    if (!tally[blockType]) {
      tally[blockType] = 0;
    }
    return `${blockType.charAt(0).toUpperCase() + blockType.slice(1)} ${++tally[blockType]}`;
  };

  return (
    <>
      <div className='flex items-center px-2'>
        <ResourceStatusIcons resource={resource} />
        <h3 className='grow text-xl text-center my-2'>{resource!.title}</h3>
        {
          onEdit && resource?.type !== ResourceType.SCRIPTURE && (
            <Button
              className='p-1 mx-2'
              grow={false}
              onClick={onEdit}
            >
              <PencilIcon className='size-5' />
            </Button>
          )
        }
      </div>
      <div className='grow overflow-y-auto px-2'>
        {
          resource!.content.map(
            (block, idx) => (
              <BlockView
                block={block}
                descriptor={getDescriptor(block)}
                key={idx}
                liveAddress={idx === liveAddress?.block ? liveAddress : undefined}
                onLineClick={onLineClick && onLineClick(idx)}
              />
            )
          )
        }
      </div>
    </>
  );
};

export const ResourceView = forwardRef(({ className, resource, ...props }: Props, ref: Ref<HTMLDivElement>) => {

  const classes = classNames(
    'bg-zinc-100 border-zinc-300 border rounded-sm grow flex flex-col min-h-0',
    className
  );

  return (
    <div
      className={classes}
      ref={ref}
    >
      <ErrorBoundary fallback={() => <span>Could not display this resource</span>}>
        {
          resource &&
            <ResourceViewInner
              resource={resource}
              {...props}
            />
        }
      </ErrorBoundary>
    </div>
  );
});
